import useDict from '../../hooks/useDict';
import {CircularProgress, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import {Resizable} from 'react-resizable';
import InformationBox from '../../components/other/informationBox';
import _ from 'lodash';
import moment from 'moment';
import config from '../../app/config';
import AgyleAccordion from '../../components/ui/accordion/agyleAccordion';

const useStyles = makeStyles((theme) => ({
    loading: {
        textAlign: 'center',
        fontWeight: 'bold',
    },
    table: {
        backgroundColor: theme.palette.background.default,
        width: '100%',
        tableLayout: 'fixed',
    },
    header: {
        fontSize: 'small',
        padding: 5,
    },
    row: {
        fontSize: 'small',
        padding: 5,
        backgroundColor: theme.palette.background.paper,
        borderBottom: 'none',
    },
    blankRow: {
        paddingBottom: 25,
        backgroundColor: theme.palette.mode === 'light' ? '#eaeaea' : '#393838',
        borderBottom: 'none',

    },
    accordion: {
        backgroundColor: theme.palette.accordion.backgroundColor,
        marginBottom: 5,
        marginTop: 6,
    },
    marginBottom: {
        marginBottom: 20,
    },
}));

function createEntry(key, name, width, date, fullDate) {
    return {key, name, width, date, fullDate};
}

const cat1 = [
    createEntry('site', 'Site', 1),
    createEntry('attendanceNumber', 'Attendance Number', 2),
    createEntry('arrivalDate', 'Arrival Date', 1, true),
    createEntry('arrivalTime', 'Arrival Time', 1),
    createEntry('reattendance', 'Reattendance', 1),
    createEntry('nhsNumber', 'NHS Number', 1),
    createEntry('sexGender', 'Sex Gender', 2),
    createEntry('referralSource', 'Referral Source', 2),
    createEntry('bookedAppointment', 'Booked Appointment', 1),
    createEntry('arrivalMode', 'Arrival Mode', 2),
];

const cat2 = [
    createEntry('fastTriageCategory', 'Fast Triage Cat.', 1),
    createEntry('fastTriageDate', 'Fast Triage Date', 1, true),
    createEntry('fastTriageTime', 'Fast Triage Time', 1),
    createEntry('timeToFastTriage', 'Time to Fast Triage', 1),
    createEntry('triageCategory', 'Triage Category', 1),
    createEntry('triageDate', 'Triage Date', 1, true),
    createEntry('triageTime', 'Triage Time', 1),
    createEntry('triageComments', 'Triage Comments', 2),
    createEntry('timeToTriage', 'Time to Triage', 1),
    createEntry('firstNews', 'First NEWS', 1),
    createEntry('presentingComplaint', 'Presenting Complaint', 1),
    createEntry('chiefComplaint', 'Chief Complaint', 1),
    createEntry('chiefComplaintParent', 'Chief Complaint Parent', 1),
];

const cat3 = [
    createEntry('arrivalToSeenMins', 'Arrival to Seen Min', 1),
    createEntry('firstClinicianSeenDate', 'First Clinician Seen Date', 1, true),
    createEntry('firstClinicianSeenTime', 'First Clinician Seen Time', 1),
    createEntry('firstClinicianSeenName', 'First Clinician Seen Name', 1),
    createEntry('seniorReviewDate', 'Senior Review Date', 1, true),
    createEntry('seniorReviewTime', 'Senior Review Time', 1),
    createEntry('diagnosisDatetime', 'Diagnosis Datetime', 1, true, true),
    createEntry('diagnosis1', 'Diagnosis 1', 3),
    createEntry('diagnosis2', 'Diagnosis 2', 1),
    createEntry('diagnosis3', 'Diagnosis 3', 1),
    createEntry('diagnosis4', 'Diagnosis 4', 1),
    createEntry('clinicianSeenToDiagnosis', 'Clinician Seen to Diagnosis', 1),
];

const cat4 = [
    createEntry('specialty', 'Specialty', 1),
    createEntry('referralDate', 'Referral Date', 1, true),
    createEntry('referralTime', 'Referral Time', 1),
    createEntry('timeToReferral', 'Time to Referral', 1),
    createEntry('seenRats', 'Seen RATS', 1),
    createEntry('ratsDate', 'RATS Date', 1, true),
    createEntry('ratsTime', 'RATS Time', 1),
    createEntry('bedBookedDatetime', 'Bed Booked Datetime', 2, true, true),
    createEntry('bedReadyDatetime', 'Bed Ready Datetime', 1, true, true),
    createEntry('timeToBedReadyFromBooked', 'Time to bed Ready from Booked', 1, true, true),
    createEntry('firstLocation', 'First Location', 1),
    createEntry('firstLocationDate', 'First Location Date', 1, true),
    createEntry('firstLocationTime', 'First Location Time', 1),
];

const cat5 = [
    createEntry('secondLocation', 'Second Location', 1),
    createEntry('secondLocationDate', 'Second Location Date', 1, true),
    createEntry('secondLocationTime', 'Second Location Time', 1),
    createEntry('beforeLastLocation', 'Before Last Location', 1),
    createEntry('beforeLastLocationDate', 'Before Last Location Date', 1, true),
    createEntry('beforeLastLocationTime', 'Before Last Location Time', 1),
    createEntry('lastLocation', 'Last Location', 1),
    createEntry('resus', 'Resus', 2),
    createEntry('ambulatory', 'Ambulatory', 1),
    createEntry('majorsMinors', 'Majors Minors', 1),
    createEntry('timeInEdMins', 'Time in ED Mins', 1),
    createEntry('timeInEdHrsMins', 'Time in ED Hrs Mins', 1),
    createEntry('twelveHoursInEd', 'Twelve Hours in ED', 1),
];

const cat6 = [
    createEntry('dischargeDate', 'Discharge Date', 1, true),
    createEntry('dischargeTime', 'Discharge Time', 1),
    createEntry('leftDeptDate', 'Left Dept Date', 1, true),
    createEntry('leftDeptTime', 'Left Dept Time', 1),
    createEntry('leftOrDischargeDate', 'Left or Discharge Date', 1, true),
    createEntry('leftOrDischargeTime', 'Left or Discharge Time', 1),
    createEntry('dischargedOnSystem', 'Discharge on System', 1),
    createEntry('dischargeOutcome', 'Discharge Outcome', 2),
    createEntry('admitted', 'Admitted', 1),
    createEntry('streamed', 'Streamed', 1),
    createEntry('dischargeDestination', 'Discharge Destination', 1),
];

const AttendanceCollection = ({patient}) => {
    const {t} = useTranslation();
    const classes = useStyles();

    const [attendances, attendancesLoading] = useDict('UHDAttendances', '', `hospitalNumber eq '${patient?.patient?.__id}' OR hospitalNumber eq '${checkHospitalId(patient?.patient?.__id)}'`);
    const [expanded, setExpanded] = useState({});

    const getDateFormatted = (date, fullDate) => {
        if (date) {
            const dateMoment = moment(date);
            return dateMoment.isValid() ? dateMoment.format(fullDate ? config.defaultDateTimeFormat : config.defaultDateFormat) : date;
        }

        return 'N/R';
    };

    const handleExpanded = (id) => {
        const exp = {...expanded};
        if (exp[id]) delete exp[id];
        else exp[id] = true;
        setExpanded(exp);
    };

    const renderList = () => {
        if (_.isEmpty(attendances)) {
            return (
                <InformationBox type="info" noMargin>
                    {t('There are no historical collection attendances for this patient.')}
                </InformationBox>
            );
        }

        const sorted = _.sortBy(attendances, [
            (item) => moment(item.arrivalDate).toDate(),
            (item) => moment(item.arrivalTime).toDate(),
        ]).reverse();

        return (
            sorted?.map(att => (
                <AgyleAccordion
                    key={att.__id}
                    label={`Attendance ${att?.attendanceNumber || 'N/R'} from ${getDateFormatted(att?.arrivalDate)}`}
                    className={classes.accordion}
                    expanded={expanded[att?.__id]}
                    onChange={() => handleExpanded(att?.__id)}
                >
                    {renderTable(att)}
                </AgyleAccordion>
            ))
        );
    };

    const renderTable = (att) => {
        return (
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        {cat1.map((c, index) => (
                            <ResizableTableCell
                                key={c.name}
                                // padding="none"
                                className={classes.header}
                                width={100}
                                colSpan={c.width}

                            >
                                {c.name}
                            </ResizableTableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    <TableRow>
                        {cat1.map((c) => (
                            <TableCell
                                key={c.key}
                                padding="none"
                                className={classes.row}
                                colSpan={c.width}
                            >
                                {c.date ? getDateFormatted(att[c.key], c.fullDate) : att[c.key]}
                            </TableCell>
                        ))}
                    </TableRow>
                    {blankSpace()}
                    {renderRows(cat2, att)}
                    {blankSpace()}
                    {renderRows(cat3, att)}
                    {blankSpace()}
                    {renderRows(cat4, att)}
                    {blankSpace()}
                    {renderRows(cat5, att)}
                    {blankSpace()}
                    {renderRows(cat6, att)}
                </TableBody>
            </Table>
        );
    };

    const blankSpace = () => {
        return (
            <TableCell
                padding="none"
                className={classes.blankRow}
                colSpan={14}
            >
            </TableCell>
        );
    };

    const renderRows = (cat, att) => {
        return (
            <>
                <TableRow>
                    {cat.map((c) => (
                        <ResizableTableCell
                            key={c.name}
                            className={classes.header}
                            width={100}
                            colSpan={c.width}
                        >
                            {c.name}
                        </ResizableTableCell>
                    ))}
                </TableRow>

                <TableRow>
                    {cat.map((c) => (
                        <TableCell
                            key={c.key}
                            padding="none"
                            className={classes.row}
                            colSpan={c.width}
                        >
                            {c.date ? getDateFormatted(att[c.key], c.fullDate) : att[c.key]}
                        </TableCell>
                    ))}
                </TableRow>
            </>
        );
    };

    return (
        <>
            {attendancesLoading &&
                <Grid item xs={12}>
                    <div className={classes.loading}>
                        <p>{t('Loading attendances data. Please wait.')}</p>
                        <CircularProgress/>
                    </div>
                </Grid>
            }
            {!attendancesLoading &&
                <div style={{marginTop: 6}}>
                    {renderList()}
                </div>
            }
        </>
    );
};

export default AttendanceCollection;

const ResizableTableCell = ({width, ...props}) => {
    const resizableProps = {
        width,
        height: 0,
        draggableOpts: {enableUserSelectHack: false},
    };

    return <Resizable {...resizableProps}><TableCell {...props} /></Resizable>;
};

export const checkHospitalId = (hospitalId) => {
    if (_.startsWith(_.toLower(hospitalId), '0')) {
        return hospitalId?.substring(1);
    }
    return hospitalId;
};